import { createApi } from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';

import {
  FindGraphUsersResponse,
  GetUserDirectReportsResponse,
  GetUserProfileResponse,
} from '@/typings/api';
import { setIsManager, setProfile } from '../features/session/slice';
import { RootState } from '../store';
import { env } from '@/lib/env';

const baseQuery = fetchBaseQuery({
  baseUrl: env.GRAPH_PROXY_URL,
  // Preserve OData special characters ($, (, ), ;, ,) unencoded so Graph API
  // can correctly parse system query options like $expand=manager($select=id)
  paramsSerializer: (params) =>
    Object.entries(params)
      .map(([key, value]) => {
        const encodedValue = String(value).replace(
          /[^a-zA-Z0-9$().,:;=@_~-]/g,
          encodeURIComponent
        );
        return `${key}=${encodedValue}`;
      })
      .join('&'),
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const { session, invitationToken } = state.sessionSlice;

    if (invitationToken) {
      headers.set('authorization', `Invitation ${invitationToken}`);
    } else if (session?.accessToken) {
      headers.set('authorization', `Bearer ${session.accessToken}`);
    }

    return headers;
  },
});

export const graphApi = createApi({
  reducerPath: 'graphApi',
  baseQuery,
  tagTypes: ['GraphUserProfile', 'GraphUserReports'],
  endpoints: (builder) => ({
    getUserProfile: builder.query<GetUserProfileResponse, { email: string }>({
      query: ({ email }) => ({
        url: `/v1.0/users/${email}`,
        params: {
          $expand: 'manager($levels=1;$select=id,displayName,mail)',
        },
      }),
      providesTags(_result, error, args) {
        if (error) {
          return [];
        }

        return [{ id: args.email, type: 'GraphUserProfile' }];
      },
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const { data } = await queryFulfilled;
        const state = getState() as RootState;

        if (args.email === state.sessionSlice.session?.user.email) {
          dispatch(
            graphApi.endpoints.getUserDirectReports.initiate({
              email: args.email,
            })
          );
          dispatch(setProfile(data));
        }
      },
    }),

    getUserProfileImage: builder.query<string, { email: string }>({
      query: ({ email }) => ({
        url: `/v1.0/users/${email}/photos/48x48/$value`,
        responseHandler: async (response) => {
          if (!response.ok) {
            return '';
          }

          const imageBlob = await response.blob();

          return URL.createObjectURL(imageBlob);
        },
      }),
      async onCacheEntryAdded(_args, { cacheDataLoaded, cacheEntryRemoved }) {
        try {
          const { data } = await cacheDataLoaded;

          await cacheEntryRemoved;
          URL.revokeObjectURL(data);
        } catch {
          return;
        }
      },
    }),

    getUserDirectReports: builder.query<
      GetUserDirectReportsResponse,
      { email: string }
    >({
      query: ({ email }) => ({
        url: `/v1.0/users/${email}/directReports`,
        params: {
          $select: 'userPrincipalName',
        },
      }),
      providesTags(_result, error, args) {
        if (error) {
          return [];
        }

        return [{ id: args.email, type: 'GraphUserReports' }];
      },
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const { data } = await queryFulfilled;
        const state = getState() as RootState;

        if (args.email === state.sessionSlice.session?.user.email) {
          dispatch(setIsManager(data.value.length > 0));
        }
      },
    }),

    findGraphUsers: builder.query<
      FindGraphUsersResponse,
      { search: string; filter?: string; orderBy?: string; select?: string }
    >({
      query: ({ search, filter, orderBy, select }) => ({
        url: '/beta/users',
        params: {
          $search: search,
          $filter: filter,
          orderBy,
          $select: select,
          $expand: 'manager($levels=1;$select=id,displayName,mail)',
        },
      }),
    }),
  }),
});
